import React, { useState } from 'react';
import styled from 'styled-components';
import BioText from '../components/About/BioText';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { useStaticQuery, graphql } from 'gatsby';
import { CiMail, CiFileOn } from 'react-icons/ci';
import { Seo } from '../components/SEO';

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: white;
`;

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: 0 20px 0 200px;
  flex: 1 0 auto;

  @media (max-width: 1200px) {
    padding: 0 20px 0 100px;
  }

  @media (max-width: 900px) {
    padding: 0 20px 0 40px;
  }

  @media (max-width: 600px) {
    padding: 0 20px;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  min-height: 100vh;
  gap: 200px;

  @media (max-width: 1200px) {
    gap: 40px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    min-height: auto;
    gap: 40px;
  }
`;

const LeftColumn = styled.div`
  width: 60%;
  padding: 100px 0 48px 0;

  @media (max-width: 768px) {
    width: 100%;
    padding: 48px 0 24px 0;
  }
`;

const RightColumn = styled.div`
  width: 35%;
  padding: 100px 0 48px 0;
  padding-left: 20px;

  @media (max-width: 768px) {
    width: 100%;
    padding: 48px 0 24px 0;
    border-top: 1px solid #eeeeee;
  }
`;

const SocialLinksContainer = styled.div`
  display: flex;
  gap: 2rem;
  margin:0;
`;

const SocialLink = styled.a`
  text-decoration: none;
  color: #000;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  svg {
    width: 30px;
    height: 30px;
    transition: transform 0.3s ease;
  }

  &:hover {
    svg {
      transform: translateY(-2px);
    }

    &::after {
      width: 100%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: 0;
    left: 0;
    background-color: #000;
    transition: width 0.3s ease;
  }

  @media (max-width: 768px) {
    svg {
      width: 24px; /* Slightly smaller icons on mobile */
      height: 24px;
    }
  }
`;

const Section = styled.div`
  margin-bottom: 48px;
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 400;
  text-transform: lowercase;
  margin-bottom: 24px;
  padding-bottom: 4px;
  border-bottom: 1px solid #000;
`;

const EducationItem = styled.div`
  margin-bottom: 16px;
  font-size: 14px;
`;

const SchoolName = styled.div`
  font-weight: 500;
`;

const DegreeName = styled.div`
  font-style: italic;
`;

const DateText = styled.span`
  margin-left: 8px;
`;

const AwardItem = styled.div`
  margin-bottom: 12px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const AwardText = styled.div`
  em {
    font-style: italic;
  }
`;

const MobileProfileImage = styled.div`
  display: none;
  width: 100%;
  justify-content: center;
  padding: 32px 0;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const CircleImage = styled.div`
  width: 192px;
  height: 192px;
  border-radius: 50%;
  overflow: hidden;

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`;

const BackgroundImageContainer = styled.div`
  display: none;
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: ${(props) => (props.isHovered ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  z-index: 0;

  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
    opacity: 0.3;
  }

  @media (min-width: 769px) {
    display: block;
  }
`;

const Footer = styled.div`
  height: 60px;
  width: 100%;
  background-color: white;
  border-top: 1px solid black;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-family: 'Avenir LT Std', sans-serif;
  flex-shrink: 0;
`;


const About = () => {
    const [isNameHovered, setIsNameHovered] = useState(false);
    const data = useStaticQuery(graphql`
      query {
        profileImage: file(relativePath: { eq: "me.png" }) {
          childImageSharp {
            gatsbyImageData(
              width: 192
              height: 192
              placeholder: BLURRED
              layout: CONSTRAINED
            )
          }
        }
        backgroundImage: file(relativePath: { eq: "me.png" }) {
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
          }
        }
      }
    `);

    const profileImageData = getImage(data.profileImage);
    const backgroundImageData = getImage(data.backgroundImage);

    const handleNameHover = (hovering) => {
      setIsNameHovered(hovering);
    };

  return (
    <PageContainer>
      <ContentWrapper>
        <MobileProfileImage>
          <CircleImage>
            <GatsbyImage
              image={profileImageData}
              alt="Jesse Gao"
              objectFit="cover"
            />
          </CircleImage>
        </MobileProfileImage>

        <FlexContainer>
          <BackgroundImageContainer isHovered={isNameHovered}>
            <GatsbyImage
              image={backgroundImageData}
              alt="Jesse Gao"
              objectFit="cover"
              style={{ height: '100%' }}
            />
          </BackgroundImageContainer>

          <LeftColumn>
            <BioText onNameHover={handleNameHover} />
          </LeftColumn>

          <RightColumn>
            <Section>
              <SocialLinksContainer>
                <SocialLink href="mailto:chenfenggao2029@u.northwestern.edu">
                  <CiMail />
                  <span>email me</span>
                </SocialLink>
                <SocialLink
                  href="/cv/jesse-gao-cv.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download="jesse-gao-cv.pdf"
                >
                  <CiFileOn />
                  <span>download CV</span>
                </SocialLink>
              </SocialLinksContainer>
            </Section>
            <Section>
              <SectionTitle>education</SectionTitle>
              <EducationItem>
                <SchoolName>Northwestern University</SchoolName>
                <DegreeName>
                  PhD in Technology Social Behavior (TSB){' '}
                  <DateText>2024 - Present</DateText>
                </DegreeName>
              </EducationItem>
              <EducationItem>
                <SchoolName>University of Chicago</SchoolName>
                <DegreeName>
                  Master of Science (M.S.) in Computer Science{' '}
                  <DateText>2022 - Mar 2024</DateText>
                </DegreeName>
              </EducationItem>

              <EducationItem>
                <SchoolName>IIT Institute of Design</SchoolName>
                <DegreeName>
                  Master of Design (M.Des.) in Human-computer Interaction{' '}
                  <DateText>2019 - 2021</DateText>
                </DegreeName>
              </EducationItem>

              <EducationItem>
                <SchoolName>Renmin University of China</SchoolName>
                <DegreeName>
                  Bachelor of Science (B.S.) in Math and Applied Math{' '}
                  <DateText>2013 - 2017</DateText>
                </DegreeName>
              </EducationItem>
            </Section>
            <Section>
              <SectionTitle>research experience</SectionTitle>
              <EducationItem>
                <SchoolName>Northwestern University, SPICELab</SchoolName>
                <DegreeName>
                  Advised by Dr. Karan Ahuja
                  <DateText>Mar 2024 - Present</DateText>
                </DegreeName>
              </EducationItem>
              <EducationItem>
                <SchoolName>University of Chicago, AxLab</SchoolName>
                <DegreeName>
                  Advised by Dr. Ken Nakagaki
                  <DateText>May 2022 - Apr 2024</DateText>
                </DegreeName>
              </EducationItem>
              <EducationItem>
                <SchoolName>Rush University Medical Center </SchoolName>
                <DegreeName>
                  Advised by Dr. Santosh Basapur
                  <DateText>Dec 2021 - Apr 2022</DateText>
                </DegreeName>
              </EducationItem>
              <EducationItem>
                <SchoolName>
                  Harvard University | D-Lab at T.H. Chan School of Public
                  Health{' '}
                </SchoolName>
                <DegreeName>
                  Advised by Dr. André Nogueira
                  <DateText>May 2021 - Dec 2021</DateText>
                </DegreeName>
              </EducationItem>
              <EducationItem>
                <SchoolName>Renmin University of China</SchoolName>
                <DegreeName>
                  Advised by Dr. Wei Xu
                  <DateText>Jun 2014 - Jun 2015</DateText>
                </DegreeName>
              </EducationItem>
            </Section>
            <Section>
              <SectionTitle>awards & recognition</SectionTitle>
              <AwardItem>
                <AwardText>
                  PhD Student Research Award,{' '}
                  <em>Northwestern University CS Department 2026</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  2025 User Experience Desing Winner,{' '}
                  <em>Red Dot Design Award</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  Paper+Demo Honorable Mention Award, <em>ACM-CHI 2024</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  “MobilePoser puts motion capture into mobile devices”{' '}
                  <em>The Engineer</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  "Ceiling robots can reconfigure room lights and curtains"{' '}
                  <em>NewScientist</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  Jury Honorable Mention Award, <em>ACM-UIST SIC 2022</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  Student Notable Health/Wellness Design Award, <em>Core77</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  Best Student Design of 2022, Honorable Mentions,{' '}
                  <em>Fast Company</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  Master Challenges Competition'21, Winner,{' '}
                  <em>IIT Institute of Design & Harvard D-Lab</em>
                </AwardText>
              </AwardItem>
              <AwardItem>
                <AwardText>
                  Master Challenges Competition'20, Winner,{' '}
                  <em>IIT Institute of Design & Verizon</em>
                </AwardText>
              </AwardItem>
            </Section>
          </RightColumn>
        </FlexContainer>
      </ContentWrapper>
      <Footer>Jesse Gao Assets, Design and build in 2025</Footer>
    </PageContainer>
  );
};

export default About;

export const Head = () => (
  <Seo
    title="About - Jesse Gao"
    description="Learn about Jesse Gao."
    pathname="/about/"
  />
);
